import { assertEvent } from 'xstate';

import { type LocationSearchMachineInput } from '../../types';

import type {
  AddNewDeliveryAddressActorInput,
  DefaultDeliveryLocationActorInput,
  InitialLocationsActorInput,
  LocationWarningActorInput,
  PredictionsAndRestaurantsActorInput,
  SearchByAreaActorInput,
  SearchByDeliveryAddressActorInput,
  SearchByGeoActorInput,
  SearchDeliveryAddressesActorInput,
  SelectPredictionActorInput,
} from '../actors';

// ─── Locations ───────────────────────────────────────────────────────────────

export const getInitialLocationsInput: LocationSearchMachineInput<InitialLocationsActorInput> = ({
  context,
}) => ({
  client: context.client,
  shouldRequestLocationPermission: context.shouldRequestLocationPermission,
});

export const getSearchByAreaInput: LocationSearchMachineInput<SearchByAreaActorInput> = ({
  context,
}) => ({
  client: context.client,
  context: {
    searchArea: context.searchArea,
    currentPosition: context.currentPosition,
    baseLocation: context.baseLocation,
  },
});

export const getSearchByGeoInput: LocationSearchMachineInput<SearchByGeoActorInput> = ({
  context,
}) => ({
  client: context.client,
  context: {
    searchString: context.searchString,
    predictionsSearchQuery: context.predictionsSearchQuery,
    currentPosition: context.currentPosition,
    baseLocation: context.baseLocation,
    baseLocationRadiusConfig: context.baseLocationRadiusConfig,
    searchArea: context.searchArea,
  },
});

// ─── Predictions ─────────────────────────────────────────────────────────────

export const getPredictionsAndRestaurantsInput: LocationSearchMachineInput<
  PredictionsAndRestaurantsActorInput
> = ({ context }) => ({
  client: context.client,
  predictionsSearchQuery: context.predictionsSearchQuery,
  currentPosition: context.currentPosition,
  context: {
    searchString: context.searchString,
    predictionsSearchQuery: context.predictionsSearchQuery,
    currentPosition: context.currentPosition,
    baseLocation: context.baseLocation,
    baseLocationRadiusConfig: context.baseLocationRadiusConfig,
    searchArea: context.searchArea,
  },
});

export const getSelectPredictionInput: LocationSearchMachineInput<SelectPredictionActorInput> = ({
  context,
  event,
}) => {
  assertEvent(event, 'SELECT_PREDICTION');

  return {
    predictions: context.predictions,
    event,
  };
};

// ─── Location Warning ────────────────────────────────────────────────────────

export const getLocationWarningInput: LocationSearchMachineInput<LocationWarningActorInput> = ({
  context,
}) => ({
  activeLocation: context.activeLocation,
});

// ─── Delivery Location ───────────────────────────────────────────────────────

export const getSearchByDeliveryAddressInput: LocationSearchMachineInput<
  SearchByDeliveryAddressActorInput
> = ({ context }) => ({
  client: context.client,
  address:
    context.selectedDeliveryAddressDetails ?? context.selectedDeliveryAddressPredictionDetails,
});

export const getDefaultDeliveryLocationInput: LocationSearchMachineInput<
  DefaultDeliveryLocationActorInput
> = ({ context }) => ({
  client: context.client,
  lastInteractedStoreAddressId: context.lastInteractedStoreAddressId,
});

export const getSearchDeliveryAddressesInput: LocationSearchMachineInput<
  SearchDeliveryAddressesActorInput
> = ({ context }) => ({
  client: context.client,
  deliverySearchString: context.deliverySearchString,
  currentPosition: context.currentPosition,
});

export const getAddNewDeliveryAddressInput: LocationSearchMachineInput<
  AddNewDeliveryAddressActorInput
> = ({ context }) => ({
  client: context.client,
  deliveryLocation: context.deliveryLocation,
});
